import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API_BASE, formatApiError } from "../lib/api";
import { downloadCsv } from "../lib/downloadCsv";
import {
  Search,
  Filter,
  Plus,
  Download,
  X,
  Trash2,
  CheckSquare,
  Square,
  Warehouse,
} from "lucide-react";
import {
  PROJECT_STATIONS,
  CURRENT_LOCATIONS_LEGACY,
  STATUSES,
  YARD_EXTRA_STATUSES,
  STORAGE_DETAILS,
  PREP_QUEUE_STATUSES,
  WORKFLOW_STAGES,
  WORKFLOW_COLORS,
  formatLocalDateTime,
} from "../lib/constants";
import { StatusPill } from "../components/StatusPill";
import AuthImg from "../components/AuthImg";
import StockingLocationCell from "../components/StockingLocationCell";
import DeleteShipmentDialog from "../components/DeleteShipmentDialog";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function Shipments() {
  const { isPrivileged, isGlobalAdmin, userLocation } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const missingStorage = searchParams.get("missing_storage") === "1";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [locFilter, setLocFilter] = useState("");
  const [locOptions, setLocOptions] = useState([]);
  const [filters, setFilters] = useState({
    project_station: "",
    current_location: "",
    status: "",
    workflow_stage: "",
    prep_status: "",
    storage_detail: "",
    assigned_crew_leader: "",
  });

  // Load location options for global admins (lets them scope the shipments list).
  useEffect(() => {
    if (!isGlobalAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/locations");
        setLocOptions((data?.locations || []).filter(Boolean));
      } catch {
        /* non-fatal */
      }
    })();
  }, [isGlobalAdmin]);

  const params = useMemo(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) p[k] = v;
    });
    if (missingStorage) p.missing_storage = true;
    if (isGlobalAdmin && locFilter) p.location = locFilter;
    return p;
  }, [q, filters, missingStorage, isGlobalAdmin, locFilter]);

  const clearMissingStorage = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("missing_storage");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/shipments", { params });
        setItems(data);
        // Drop selected ids that are no longer in result set
        setSelected((prev) => {
          const ids = new Set(data.map((x) => x.id));
          const keep = new Set([...prev].filter((id) => ids.has(id)));
          return keep;
        });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [params]);

  const clearFilters = () => {
    setFilters({ project_station: "", current_location: "", status: "", workflow_stage: "", prep_status: "", storage_detail: "", assigned_crew_leader: "" });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((s) => s.id)));
    }
  };
  const clearSelection = () => setSelected(new Set());

  const doBulkDelete = async (reason, notes, confirm) => {
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/shipments/bulk-delete", { ids, reason, notes, confirm });
      toast.success(`Deleted ${data.deleted_count} shipment${data.deleted_count === 1 ? "" : "s"}`);
      setBulkOpen(false);
      setSelected(new Set());
      // Reload list
      const refreshed = await api.get("/shipments", { params });
      setItems(refreshed.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Pass current search + filter params so the export reflects what the user sees.
      const exportParams = { ...params };
      // If selection is non-empty, export only the selected ids (overrides filters server-side).
      if (selected.size > 0) {
        exportParams.ids = Array.from(selected).join(",");
      }
      await downloadCsv({
        url: "/shipments/export",
        params: exportParams,
        fallbackFilename: `Material_Shipments_${new Date().toISOString().slice(0,10)}.csv`,
      });
    } catch (e) {
      // The downloadCsv helper already shows a toast for the user; log here so
      // the failure surfaces in dev tools and any monitoring instrumentation.
      console.error("Shipments CSV export failed", e);
    } finally {
      setExporting(false);
    }
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">
            All Shipments
          </div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase text-slate-900 tracking-tight">
            Find / Update
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportCsv}
            disabled={exporting}
            data-testid="export-csv-btn"
            className="rg-tap px-3 sm:px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-slate-700 disabled:opacity-50"
            title={selected.size > 0 ? `Export ${selected.size} selected row${selected.size === 1 ? "" : "s"}` : "Export all matching rows"}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">
              {exporting ? "Exporting…" : selected.size > 0 ? `CSV (${selected.size})` : "CSV"}
            </span>
          </button>
          <Link
            to="/shipments/receive"
            data-testid="add-shipment-btn"
            className="rg-tap px-3 sm:px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-sm"
          >
            <Plus strokeWidth={3} className="h-4 w-4" />
            <span className="hidden sm:inline">Add Shipment</span>
            <span className="sm:hidden">Add</span>
          </Link>
        </div>
      </div>

      {/* Yard Cleanup mode banner — active when ?missing_storage=1 */}
      {missingStorage && (
        <div
          className="rg-card mb-4 p-4 bg-amber-50 border-2 border-amber-400 flex items-start gap-3"
          data-testid="missing-storage-banner"
        >
          <div className="h-10 w-10 grid place-items-center bg-amber-500 text-slate-900 rounded-sm flex-shrink-0 border-2 border-amber-700">
            <Warehouse strokeWidth={2.5} className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">
              Yard Cleanup
            </div>
            <div className="font-display text-base sm:text-lg uppercase text-amber-900 leading-tight">
              Showing shipments at Main yard with no Stocking Location
            </div>
            <p className="text-xs sm:text-sm text-amber-900/80 mt-0.5">
              Open each row to set a Bay/Rack/Shelf, or use the inline editor on the Runner Queue.
            </p>
          </div>
          <button
            type="button"
            onClick={clearMissingStorage}
            data-testid="missing-storage-clear-btn"
            className="rg-tap px-3 border-2 border-amber-700 hover:border-amber-900 hover:bg-amber-100 rounded-sm bg-white flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-900 flex-shrink-0"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        </div>
      )}

      {/* Search & filters */}
      <div className="rg-card p-4 mb-4">
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="shipments-search"
              placeholder="Search MR or contents…"
              className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white text-base focus:border-slate-900 outline-none"
            />
          </div>
          <button
            onClick={() => setFilterOpen((s) => !s)}
            data-testid="toggle-filters-btn"
            className="rg-tap px-3 sm:px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-slate-700"
          >
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-amber-500 text-slate-900 text-xs px-1.5 rounded-sm">
                {activeFilterCount}
              </span>
            )}
          </button>
          {isGlobalAdmin && locOptions.length > 0 && (
            <select
              value={locFilter}
              onChange={(e) => setLocFilter(e.target.value)}
              data-testid="shipments-location-filter"
              className="rg-tap px-2 border-2 border-slate-300 rounded-sm bg-white text-xs font-bold uppercase tracking-wide text-slate-700"
              title="Filter by location (global admin)"
            >
              <option value="">All locations</option>
              {locOptions.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          )}
          {!isGlobalAdmin && userLocation && (
            <span
              data-testid="shipments-location-pill"
              className="rg-tap px-3 border-2 border-slate-300 rounded-sm bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-700 inline-flex items-center"
            >
              {userLocation}
            </span>
          )}
        </div>

        {filterOpen && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3" data-testid="filters-panel">
            <FilterSelect
              label="Project / Station"
              testid="filter-project-station"
              value={filters.project_station}
              onChange={(v) => setFilters({ ...filters, project_station: v })}
              options={PROJECT_STATIONS}
            />
            <FilterSelect
              label="Current Location"
              testid="filter-current-location"
              value={filters.current_location}
              onChange={(v) => setFilters({ ...filters, current_location: v })}
              options={CURRENT_LOCATIONS_LEGACY}
            />
            <FilterSelect
              label="Workflow Stage"
              testid="filter-workflow-stage"
              value={filters.workflow_stage}
              onChange={(v) => setFilters({ ...filters, workflow_stage: v })}
              options={WORKFLOW_STAGES}
            />
            <FilterSelect
              label="Status"
              testid="filter-status"
              value={filters.status}
              onChange={(v) => setFilters({ ...filters, status: v })}
              options={[...STATUSES, ...YARD_EXTRA_STATUSES]}
            />
            <FilterSelect
              label="Prep Queue"
              testid="filter-prep-status"
              value={filters.prep_status}
              onChange={(v) => setFilters({ ...filters, prep_status: v })}
              options={PREP_QUEUE_STATUSES}
            />
            <FilterSelect
              label="Storage Detail"
              testid="filter-storage-detail"
              value={filters.storage_detail}
              onChange={(v) => setFilters({ ...filters, storage_detail: v })}
              options={STORAGE_DETAILS}
            />
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                Crew Leader
              </label>
              <input
                value={filters.assigned_crew_leader}
                onChange={(e) =>
                  setFilters({ ...filters, assigned_crew_leader: e.target.value })
                }
                data-testid="filter-crew-leader"
                placeholder="Search by name…"
                className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
              />
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                data-testid="clear-filters-btn"
                className="col-span-full sm:col-span-2 lg:col-span-6 text-sm text-slate-600 hover:text-slate-900 font-bold uppercase tracking-wide flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bulk action bar — admin only, visible when at least 1 selected */}
      {isPrivileged && selected.size > 0 && (
        <div
          className="sticky top-[60px] z-30 mb-3 rg-card p-3 border-2 border-rose-300 bg-rose-50 flex flex-wrap items-center justify-between gap-3"
          data-testid="bulk-action-bar"
        >
          <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <CheckSquare className="h-4 w-4 text-rose-700" />
            <span data-testid="bulk-selected-count">{selected.size}</span>
            <span>selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              data-testid="bulk-clear-btn"
              className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white text-xs font-bold uppercase tracking-wide text-slate-700"
            >
              Clear
            </button>
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-delete-btn"
              className="rg-tap px-3 bg-rose-700 hover:bg-rose-800 text-white rounded-sm flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide border-2 border-rose-900"
            >
              <Trash2 strokeWidth={3} className="h-3.5 w-3.5" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Results: mobile cards / desktop table */}
      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rg-card p-10 text-center" data-testid="shipments-empty">
          <div className="font-display text-xl text-slate-900 uppercase mb-1">No matches</div>
          <div className="text-sm text-slate-600">Try adjusting search or filters.</div>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rg-card overflow-x-auto" data-testid="shipments-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b-2 border-slate-300 bg-slate-50">
                  {isPrivileged && (
                    <th className="py-3 px-2 w-10">
                      <button
                        type="button"
                        onClick={toggleAll}
                        data-testid="bulk-select-all-btn"
                        className="rg-tap p-1.5 hover:bg-slate-200 rounded-sm"
                        title={selected.size === items.length ? "Deselect all" : "Select all"}
                        aria-label="Select all rows"
                      >
                        {selected.size === items.length && items.length > 0 ? (
                          <CheckSquare strokeWidth={2.5} className="h-4 w-4 text-rose-700" />
                        ) : (
                          <Square strokeWidth={2.5} className="h-4 w-4 text-slate-500" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="py-3 px-3">MR</th>
                  <th className="py-3 px-3">Workflow</th>
                  {isGlobalAdmin && <th className="py-3 px-3">Location</th>}
                  <th className="py-3 px-3">Project / Station</th>
                  <th className="py-3 px-3">Crew Leader</th>
                  <th className="py-3 px-3">Current Location</th>
                  <th className="py-3 px-3">Stocking Location</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3">Prep Queue</th>
                  <th className="py-3 px-3">Contents</th>
                  <th className="py-3 px-3">Minors</th>
                  <th className="py-3 px-3">Equipment</th>
                  <th className="py-3 px-3">Images</th>
                  <th className="py-3 px-3">Received</th>
                  <th className="py-3 px-3">Last Updated</th>
                  <th className="py-3 px-3">By</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr
                    key={s.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${selected.has(s.id) ? "bg-rose-50/40" : ""}`}
                    data-testid={`row-${s.MR_Number}`}
                  >
                    {isPrivileged && (
                      <td className="py-2 px-2 align-middle">
                        <button
                          type="button"
                          onClick={() => toggleOne(s.id)}
                          data-testid={`row-select-${s.MR_Number}`}
                          aria-label={`Select ${s.MR_Number}`}
                          className="rg-tap p-1.5 hover:bg-slate-200 rounded-sm"
                        >
                          {selected.has(s.id) ? (
                            <CheckSquare strokeWidth={2.5} className="h-4 w-4 text-rose-700" />
                          ) : (
                            <Square strokeWidth={2.5} className="h-4 w-4 text-slate-400" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="py-2 px-3">
                      <Link
                        to={`/shipments/${s.id}`}
                        className="font-mono font-bold text-slate-900 hover:underline"
                      >
                        {s.MR_Number}
                      </Link>
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border-2 rounded-sm ${WORKFLOW_COLORS[s.Workflow_Stage] || "text-slate-600 bg-slate-100 border-slate-300"}`}
                      >
                        {s.Workflow_Stage || "—"}
                      </span>
                    </td>
                    {isGlobalAdmin && (
                      <td className="py-2 px-3">
                        <span
                          data-testid={`row-location-${s.MR_Number}`}
                          className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border-2 rounded-sm bg-slate-50 text-slate-700 border-slate-300"
                        >
                          {s.location || "—"}
                        </span>
                      </td>
                    )}
                    <td className="py-2 px-3 text-slate-700">{s.Project_Station}</td>
                    <td className="py-2 px-3 text-slate-700 font-bold">
                      {s.Assigned_Crew_Leader || <span className="text-slate-400 font-normal">—</span>}
                    </td>
                    <td className="py-2 px-3 text-slate-700">
                      {s.Current_Location}
                      {s.Current_Location === "Other" && s.Other_Location
                        ? ` · ${s.Other_Location}`
                        : ""}
                    </td>
                    <td
                      className={`py-2 px-3 ${
                        s.Current_Location === "Main yard" && !s.Storage_Detail
                          ? "bg-amber-50"
                          : ""
                      }`}
                    >
                      <StockingLocationCell shipment={s} />
                    </td>
                    <td className="py-2 px-3">
                      <StatusPill value={s.Status} kind="status" />
                    </td>
                    <td className="py-2 px-3">
                      <StatusPill value={s.Preparation_Queue_Status} kind="prep" />
                    </td>
                    <td className="py-2 px-3 text-slate-700 max-w-[200px] truncate">
                      {s.Contents || "—"}
                    </td>
                    <td className="py-2 px-3 text-slate-700 max-w-[180px] truncate">
                      {s.Minor_Materials || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-3 text-slate-700 max-w-[160px] truncate">
                      {s.Equipment_Needed ? (
                        <span className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-sm font-bold">
                          {s.Equipment_Needed}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex -space-x-1">
                        {(s.Images || []).slice(0, 3).map((p) => (
                          <AuthImg
                            key={p}
                            path={p}
                            className="h-9 w-9 object-cover border-2 border-white rounded-sm"
                          />
                        ))}
                        {(s.Images || []).length > 3 && (
                          <div className="h-9 w-9 grid place-items-center bg-slate-200 text-slate-600 text-xs font-bold border-2 border-white rounded-sm">
                            +{s.Images.length - 3}
                          </div>
                        )}
                        {(s.Images || []).length === 0 && (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">
                      {formatLocalDateTime(s.Received_DateTime)}
                    </td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap">
                      {formatLocalDateTime(s.Last_Updated)}
                    </td>
                    <td className="py-2 px-3 text-slate-600">
                      {s.Last_Updated_By || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="md:hidden space-y-3" data-testid="shipments-mobile-list">
            {items.map((s) => (
              <li key={s.id} className="relative">
                {isPrivileged && (
                  <button
                    type="button"
                    onClick={() => toggleOne(s.id)}
                    data-testid={`mobile-row-select-${s.MR_Number}`}
                    aria-label={`Select ${s.MR_Number}`}
                    className="absolute top-2 right-2 z-10 rg-tap p-2 bg-white border-2 border-slate-300 rounded-sm"
                  >
                    {selected.has(s.id) ? (
                      <CheckSquare strokeWidth={2.5} className="h-4 w-4 text-rose-700" />
                    ) : (
                      <Square strokeWidth={2.5} className="h-4 w-4 text-slate-400" />
                    )}
                  </button>
                )}
                <Link
                  to={`/shipments/${s.id}`}
                  data-testid={`mobile-row-${s.MR_Number}`}
                  className={`block rg-card p-4 hover:border-slate-900 ${selected.has(s.id) ? "ring-2 ring-rose-400" : ""}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-lg font-bold text-slate-900">
                      {s.MR_Number}
                    </div>
                    <StatusPill value={s.Status} kind="status" />
                  </div>
                  <div className="text-sm text-slate-700 font-bold">{s.Project_Station}</div>
                  {s.Assigned_Crew_Leader && (
                    <div className="text-xs text-slate-900 font-bold mt-0.5">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">
                        Give to:
                      </span>
                      {s.Assigned_Crew_Leader}
                    </div>
                  )}
                  <div className="text-sm text-slate-600 flex items-center gap-1.5 flex-wrap">
                    <span>{s.Current_Location}</span>
                    {s.Current_Location === "Other" && s.Other_Location && (
                      <span>· {s.Other_Location}</span>
                    )}
                    {s.Current_Location === "Main yard" && (
                      <StockingLocationCell shipment={s} variant="cell" />
                    )}
                  </div>
                  {s.Contents && (
                    <div className="text-sm text-slate-700 mt-1 truncate">{s.Contents}</div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <StatusPill value={s.Preparation_Queue_Status} kind="prep" />
                    <span className="text-xs text-slate-500">
                      {formatLocalDateTime(s.Last_Updated)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Bulk delete confirmation — admin/supervisor only */}
      <DeleteShipmentDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        bulkCount={selected.size}
        busy={bulkBusy}
        onConfirm={doBulkDelete}
        testIdPrefix="bulk-delete"
      />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
